import React, { useState, useEffect } from 'react';
import { View, ScrollView, Platform } from 'react-native';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Card } from '~/components/gluestack/card';
import { Heading } from '~/components/gluestack/heading';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Badge, BadgeText } from '~/components/gluestack/badge';
import { NotFoundContent } from '@/components/ui/NotFoundContent';
import { useSignalR, useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { useAuth } from '@/components/providers/AuthProvider';
import { useToastMessages } from '@/hooks/useToastMessages';

function isDeveloperTestUser(userName?: string | null): boolean {
  return userName?.toLowerCase().startsWith('testuser') ?? false;
}

function SignalRTestContent() {
  const { isConnected, isConnecting, connectionError, connect, disconnect, getConnectionInfo } =
    useSignalR();
  const { messageCount, notificationCount } = useSignalRUpdates();
  const { isAuthenticated, currentCommunity } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [connectionInfo, setConnectionInfo] = useState(getConnectionInfo());

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[SignalR] [${timestamp}] ${message}`);
  };

  useEffect(() => {
    addLog(`Component mounted - Platform: ${Platform.OS}`);
  }, []);

  useEffect(() => {
    addLog(`Connection status: ${isConnected ? 'Connected' : 'Disconnected'}`);
    setConnectionInfo(getConnectionInfo());
  }, [isConnected, getConnectionInfo]);

  useEffect(() => {
    addLog(`Connecting status: ${isConnecting ? 'Connecting' : 'Not connecting'}`);
    setConnectionInfo(getConnectionInfo());
  }, [isConnecting, getConnectionInfo]);

  useEffect(() => {
    if (connectionError) {
      addLog(`Connection error: ${connectionError}`);
    }
  }, [connectionError]);

  useEffect(() => {
    addLog(`Message count updated: ${messageCount}`);
  }, [messageCount]);

  useEffect(() => {
    addLog(`Notification count updated: ${notificationCount}`);
  }, [notificationCount]);

  const handleConnect = async () => {
    try {
      addLog('Manual connect requested');
      await connect();
      addLog('Manual connect completed successfully');
      showSuccessToast('Connected successfully');
    } catch (error) {
      addLog(`Connect error: ${error}`);
      showErrorToast('Failed to connect', error);
    }
  };

  const handleDisconnect = async () => {
    try {
      addLog('Manual disconnect requested');
      await disconnect();
      addLog('Manual disconnect completed successfully');
      showSuccessToast('Disconnected successfully');
    } catch (error) {
      addLog(`Disconnect error: ${error}`);
      showErrorToast('Failed to disconnect', error);
    }
  };

  

  const getConnectionStatusText = () => {
    if (isConnecting) return 'Connecting';
    if (isConnected) return 'Connected';
    if (connectionError) return 'Error';
    return 'Disconnected';
  };

  const getConnectionStatusColor = (): 'success' | 'warning' | 'muted' | 'info' | 'error' => {
    if (isConnecting) return 'warning';
    if (isConnected) return 'success';
    if (connectionError) return 'error';
    return 'muted';
  };

  return (
    <>
      <ScreenHeader options={{ title: 'SignalR Diagnostics' }} />
      <ScrollView>
        <View className="grid grid-cols-1 gap-3 p-3 md:pt-0 xl:grid-cols-2">
          {/* Connection Status Card */}
          <Card>
            <Heading className="mb-4">
              <Text>Connection Status</Text>
            </Heading>

            <VStack space="md">
              <HStack className="items-center justify-between">
                <Text>Authentication:</Text>
                <Badge action={isAuthenticated ? 'success' : 'error'}>
                  <BadgeText>{isAuthenticated ? 'Authenticated' : 'Not Authenticated'}</BadgeText>
                </Badge>
              </HStack>

              <HStack className="items-center justify-between">
                <Text>Connection:</Text>
                <Badge action={getConnectionStatusColor()}>
                  <BadgeText>{getConnectionStatusText()}</BadgeText>
                </Badge>
              </HStack>

              <HStack className="items-center justify-between">
                <Text>Community:</Text>
                <Badge action={currentCommunity ? 'info' : 'muted'}>
                  <BadgeText>{currentCommunity || 'None'}</BadgeText>
                </Badge>
              </HStack>

              <HStack className="items-center justify-between">
                <Text>Platform:</Text>
                <Badge action="info">
                  <BadgeText>{Platform.OS}</BadgeText>
                </Badge>
              </HStack>

              <HStack className="items-center justify-between">
                <Text>Transport:</Text>
                <Badge action={connectionInfo.transport !== 'None' ? 'success' : 'muted'}>
                  <BadgeText>{connectionInfo.transport}</BadgeText>
                </Badge>
              </HStack>

              {connectionInfo.connectionId && (
                <HStack className="items-center justify-between">
                  <Text>Connection ID:</Text>
                  <Badge action="info">
                    <BadgeText>{connectionInfo.connectionId.substring(0, 8)}...</BadgeText>
                  </Badge>
                </HStack>
              )}

              <HStack className="items-center justify-between">
                <Text>State Code:</Text>
                <Badge action="info">
                  <BadgeText>{connectionInfo.state ?? 'Unknown'}</BadgeText>
                </Badge>
              </HStack>

              {connectionError && (
                <HStack className="items-center justify-between">
                  <Text>Error:</Text>
                  <Badge action="error">
                    <BadgeText>{connectionError}</BadgeText>
                  </Badge>
                </HStack>
              )}
            </VStack>
          </Card>

          {/* Real-time Data Card */}
          <Card>
            <Heading className="mb-4">
              <Text>Real-time Data</Text>
            </Heading>

            <VStack space="md">
              <HStack className="items-center justify-between">
                <Text>Message Count:</Text>
                <Badge action="info">
                  <BadgeText>{messageCount}</BadgeText>
                </Badge>
              </HStack>

              <HStack className="items-center justify-between">
                <Text>Notification Count:</Text>
                <Badge action="warning">
                  <BadgeText>{notificationCount}</BadgeText>
                </Badge>
              </HStack>
            </VStack>
          </Card>

          {/* Controls Card */}
          <Card className="xl:col-span-2">
            <Heading className="mb-4">
              <Text>Controls</Text>
            </Heading>

            <VStack space="sm">
              <HStack space="sm" className="flex-wrap">
                <Button
                  action="positive"
                  onPress={handleConnect}
                  disabled={!isAuthenticated || isConnected || isConnecting}>
                  <ButtonText>Connect</ButtonText>
                </Button>

                <Button action="negative" onPress={handleDisconnect} disabled={!isConnected}>
                  <ButtonText>Disconnect</ButtonText>
                </Button>
              </HStack>

              {!isAuthenticated && (
                <Text size="sm" className="text-error-600">
                  Note: You must be authenticated to test SignalR connection.
                </Text>
              )}
            </VStack>
          </Card>

          
        </View>
      </ScrollView>
    </>
  );
}

export default function SignalRTest() {
  const { accountStatus } = useAuth();

  if (!isDeveloperTestUser(accountStatus?.UserName)) {
    return (
      <>
        <ScreenHeader options={{ title: 'SignalR Diagnostics' }} />
        <NotFoundContent />
      </>
    );
  }

  return <SignalRTestContent />;
}
